/**
 * Dates: the API speaks ISO `YYYY-MM-DD`; the UI always shows `MM-DD-YYYY` (firm standard).
 * "Today" is the firm's business date in America/New_York, regardless of the viewer's zone.
 */
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);

export const FIRM_TIMEZONE = 'America/New_York';
export const DISPLAY_DATE = 'MM-DD-YYYY';
export const ISO_DATE = 'YYYY-MM-DD';

/** `2026-10-05` → `10-05-2026`. Empty/invalid input → `''`. */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = dayjs(iso.slice(0, 10), ISO_DATE, true);
  return d.isValid() ? d.format(DISPLAY_DATE) : '';
}

/** Timestamp → `10-05-2026 02:30 PM` in the firm's timezone. */
export function formatDateTime(isoInstant: string | null | undefined): string {
  if (!isoInstant) return '';
  const d = dayjs(isoInstant);
  return d.isValid() ? d.tz(FIRM_TIMEZONE).format(`${DISPLAY_DATE} hh:mm A`) : '';
}

/** `10-05-2026` → `2026-10-05`, or `null` when it is not a real date. */
export function parseDisplayDate(display: string): string | null {
  const d = dayjs(display.trim(), DISPLAY_DATE, true);
  return d.isValid() ? d.format(ISO_DATE) : null;
}

/** The firm's business date as ISO, e.g. `2026-10-05`. */
export function firmTodayISO(now: Date = new Date()): string {
  return dayjs(now).tz(FIRM_TIMEZONE).format(ISO_DATE);
}
